import { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Typography, Space, Skeleton, Container } from '@ag1/ui';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import styles from './SliderProduct.module.scss';
import SliderBtnControls from './SliderBtnControls';
import { SliderProductProps } from './SliderProduct.types';
import SliderProductSlide from './SliderProductSlide';

const SliderProduct = <ItemType extends { [key: string]: any }>({
  title,
  description,
  controls = true,
  slides,
  renderSlide,
  isLoading,
  placeholder,
  placeholdersCount
}: SliderProductProps<ItemType>) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      slidesToScroll: 1,
      containScroll: 'trimSnaps',
      align: 'start',
      watchDrag: true,
      skipSnaps: true
    },
    [WheelGesturesPlugin()]
  );
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setPrevBtnEnabled(emblaApi.canScrollPrev());
    setNextBtnEnabled(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  const placeholdersSlides = new Array(placeholdersCount)
    .fill(null)
    .map((_, index) => ({ id: index.toString() }));

  return (
    <section aria-roledescription="carousel">
      <Container>
        <div className={styles.head}>
          <div className={styles.info}>
            {isLoading ? (
              <Space top={{ xxs: 4, xs: 24, m: 36 }} bottom={{ xxs: 4, xs: 12, m: 24 }}>
                <Skeleton width={280} height={56} />
              </Space>
            ) : (
              <Typography variant="headlineSans" size="3" component="h3" noPadding>
                {title}
              </Typography>
            )}
            {description && (
              <>
                {isLoading ? (
                  <Space top={{ xxs: 4, xs: 8 }} bottom={{ xxs: 20 }}>
                    <Skeleton width={320} height={24} />
                  </Space>
                ) : (
                  <Typography variant="portalText" size="1" noPadding>
                    {description}
                  </Typography>
                )}
              </>
            )}
          </div>
          {controls && (
            <div className={styles.buttons}>
              <SliderBtnControls
                ariaLabel="Previous Slide"
                direction="left"
                onClick={scrollPrev}
                disabled={!prevBtnEnabled}
                className={styles.btn}
              />
              <SliderBtnControls
                ariaLabel="Next Slide"
                direction="right"
                onClick={scrollNext}
                disabled={!nextBtnEnabled}
                className={styles.btn}
              />
            </div>
          )}
        </div>
        <div className={styles.viewport} ref={emblaRef}>
          <div className={styles.container}>
            {isLoading
              ? placeholdersSlides.map(({ id }) => (
                  <SliderProductSlide key={id} className={styles.slide}>
                    {placeholder}
                  </SliderProductSlide>
                ))
              : slides.map((item) => {
                  const slide = renderSlide(item);

                  return (
                    slide && (
                      <SliderProductSlide key={item.id} className={styles.slide}>
                        {slide}
                      </SliderProductSlide>
                    )
                  );
                })}
          </div>
        </div>
      </Container>
    </section>
  );
};

export default SliderProduct;
